import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FaBell, FaEnvelope } from "react-icons/fa";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { EnhancedCard } from "@/components/ui/enhanced-card";
import { useUserPreferences } from "@/context/UserPreferencesContext";
import { useGroup } from "@/lib/context/GroupContext";
import { useToast } from "@/hooks/use-toast";
import { subscribeToPush, unsubscribeFromPush } from "@/hooks/usePushNotifications";
import { isPushSupported } from "@/lib/push/getPushRegistration";

export function NotificationSettingsSection() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { preferences, updateNotificationSettings, isLoading } = useUserPreferences();
  const { currentGroup } = useGroup();
  const [busy, setBusy] = useState<"browser" | "email" | null>(null);

  const browserOn = preferences.notification_settings.browser !== false;
  const emailOn = preferences.notification_settings.email !== false;
  const pushSupported = isPushSupported();

  const handleBrowserToggle = async (checked: boolean) => {
    if (!pushSupported && checked) {
      toast({
        title: t("notifications.browserUnsupportedTitle"),
        description: t("notifications.browserUnsupportedDesc"),
        variant: "destructive",
      });
      return;
    }

    setBusy("browser");
    try {
      await updateNotificationSettings({ browser: checked }, { silent: true });

      if (checked && currentGroup?.id) {
        const ok = await subscribeToPush(currentGroup.id);
        if (!ok) {
          await updateNotificationSettings({ browser: false }, { silent: true });
          toast({
            title: t("notifications.browserDeniedTitle"),
            description: t("notifications.browserDeniedDesc"),
            variant: "destructive",
          });
          return;
        }
      } else if (!checked && currentGroup?.id) {
        await unsubscribeFromPush(currentGroup.id);
      }

      toast({
        title: t("common.success"),
        description: checked
          ? t("notifications.browserEnabled")
          : t("notifications.browserDisabled"),
      });
    } catch (err) {
      toast({
        title: t("errors.error"),
        description: err instanceof Error ? err.message : t("errors.error"),
        variant: "destructive",
      });
    } finally {
      setBusy(null);
    }
  };

  const handleEmailToggle = async (checked: boolean) => {
    setBusy("email");
    try {
      await updateNotificationSettings({ email: checked });
      toast({
        title: t("common.success"),
        description: checked
          ? t("notifications.emailEnabled")
          : t("notifications.emailDisabled"),
      });
    } catch (err) {
      toast({
        title: t("errors.error"),
        description: err instanceof Error ? err.message : t("errors.error"),
        variant: "destructive",
      });
    } finally {
      setBusy(null);
    }
  };

  return (
    <EnhancedCard hoverable className="w-full">
      <EnhancedCard.Header>
        <EnhancedCard.Title className="flex items-center gap-2">
          <FaBell className="h-5 w-5 text-soccer-primary" />
          {t("notifications.settingsTitle")}
        </EnhancedCard.Title>
        <EnhancedCard.Description>{t("notifications.settingsDescription")}</EnhancedCard.Description>
      </EnhancedCard.Header>
      <EnhancedCard.Content className="space-y-6">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <FaBell className="h-4 w-4 text-soccer-primary shrink-0" />
            <div>
              <Label htmlFor="browser-notifications" className="font-medium text-sm">
                {t("notifications.browserLabel")}
              </Label>
              <p className="text-xs text-muted-foreground">{t("notifications.browserHint")}</p>
            </div>
          </div>
          <Switch
            id="browser-notifications"
            checked={browserOn}
            disabled={isLoading || busy === "browser" || (!pushSupported && !browserOn)}
            onCheckedChange={handleBrowserToggle}
          />
        </div>
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <FaEnvelope className="h-4 w-4 text-soccer-primary shrink-0" />
            <div>
              <Label htmlFor="email-notifications" className="font-medium text-sm">
                {t("notifications.emailLabel")}
              </Label>
              <p className="text-xs text-muted-foreground">{t("notifications.emailHint")}</p>
            </div>
          </div>
          <Switch
            id="email-notifications"
            checked={emailOn}
            disabled={isLoading || busy === "email"}
            onCheckedChange={handleEmailToggle}
          />
        </div>
      </EnhancedCard.Content>
    </EnhancedCard>
  );
}
